/**
 * Welle 14 Polish — User-Activity-Log Recorder.
 *
 * Fire-and-forget Helper für key user actions. Aufruferseite muss nicht warten.
 * Failures werden via logCaughtError geloggt, nicht geworfen — die Action selbst
 * (Trade-Plan-Save etc.) darf nie wegen Audit-Log-Fail scheitern.
 */

import { logCaughtError } from './error-log';
import { supabase } from './supabase';

export type UserActionType =
  | 'trade_plan_saved'
  | 'trade_plan_cancelled'
  | 'position_opened'
  | 'position_closed'
  | 'watchlist_add'
  | 'watchlist_remove'
  | 'watchlist_edit'
  | 'backtest_run'
  | 'strategy_param_changed'
  | 'settings_updated';

export interface UserActionRow {
  id: number;
  occurred_at: string;
  action_type: UserActionType;
  symbol: string | null;
  details_summary: string | null;
  ref_id: string | null;
  user_email: string | null;
}

interface RecordInput {
  action_type: UserActionType;
  symbol?: string | null;
  details_summary?: string | null;
  ref_id?: string | null;
  user_email?: string | null;
}

/**
 * Loggt eine User-Aktion ins `v5_user_actions`-Log. Fire-and-forget.
 * Aufruferseite: `void recordUserAction({ ... })` — kein await nötig.
 */
export async function recordUserAction(input: RecordInput): Promise<void> {
  try {
    const { error } = await supabase.from('v5_user_actions').insert({
      action_type: input.action_type,
      symbol: input.symbol ?? null,
      details_summary: input.details_summary ?? null,
      ref_id: input.ref_id ?? null,
      user_email: input.user_email ?? null,
    });
    if (error) {
      logCaughtError('user-actions.recordUserAction', error, {
        severity: 'warn',
        context: { action_type: input.action_type, symbol: input.symbol },
      });
    }
  } catch (err) {
    logCaughtError('user-actions.recordUserAction', err, {
      severity: 'warn',
      context: { action_type: input.action_type },
    });
  }
}

/**
 * Holt die jüngsten N Aktionen, neueste zuerst.
 */
export async function fetchRecentUserActions(
  limit: number = 100,
  signal?: AbortSignal
): Promise<UserActionRow[]> {
  if (signal?.aborted) return [];
  const { data, error } = await supabase
    .from('v5_user_actions')
    .select('id, occurred_at, action_type, symbol, details_summary, ref_id, user_email')
    .order('occurred_at', { ascending: false })
    .limit(limit)
    .returns<UserActionRow[]>();
  if (error) {
    logCaughtError('user-actions.fetchRecent', error, {
      severity: 'warn',
    });
    return [];
  }
  return data ?? [];
}
